/* eslint-disable no-unused-vars */
import { useQuery } from '@tanstack/react-query';
import ApiError from 'errors/ApiError';
import { useMemo } from 'react';
import { useUserData } from 'user/hooks/useUserData';

/*
  Feature Tiers (BT-9728/BT-9734). Lists the recognised tiers from GET /admin/tiers so the
  create-account wizard can offer a Tier picker. Response body: { message, tiers: [{ tier_id,
  name, description, is_default, feature_keys }] }. Tiers are Product-managed (changed only via
  DB migration), so they're effectively static within a session — hence a long staleTime.
*/

const fetchTiers = async (token) => {
  const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/tiers`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new ApiError(response, result);
  }

  return { response, tiers: result.tiers || [] };
};

export const getQueryKey = (admin_id) => [admin_id, 'featureTiers'];

export function useTiersList() {
  const {
    userData,
  } = useUserData();

  const {
    token,
    admin_id,
  } = userData;

  const key = useMemo(() => getQueryKey(admin_id), [admin_id]);

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchTiers(token),
    staleTime: 5 * 60 * 1000,
  });

  const tiers = useMemo(() => query?.data?.tiers || [], [query.data]);
  const defaultTier = useMemo(() => tiers.find((tier) => tier.is_default) || null, [tiers]);

  return {
    tiers,
    defaultTier,
    isLoading: query.isLoading,
  };
}
